import { useState, useEffect } from "react";
import { Target, Shield, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { getTeamStats } from "../lib/api";
import { toast } from "sonner";

export default function TeamStats() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await getTeamStats();
      setTeams(response.data);
    } catch (error) {
      toast.error("Failed to fetch team stats");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Find max values for visual comparison bars
  const maxGoalsFor = Math.max(...teams.map((t) => t.goals_for), 1);
  const maxGoalsAgainst = Math.max(...teams.map((t) => t.goals_against), 1);

  return (
    <div data-testid="team-stats-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
          Team Statistics
        </h1>
        <p className="text-muted-foreground mt-2">
          Detailed performance metrics for all teams
        </p>
      </div>

      {/* Stats Grid */}
      {teams.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.map((team, index) => (
            <div
              key={team.team_id}
              className={`bg-card border border-border rounded-xl overflow-hidden card-hover animate-fade-in opacity-0`}
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`team-stat-card-${team.team_id}`}
            >
              {/* Header */}
              <div className="p-6 border-b border-border">
                <div className="flex items-center gap-4">
                  {team.team_logo ? (
                    <img
                      src={team.team_logo}
                      alt={team.team_name}
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-secondary rounded-xl flex items-center justify-center">
                      <Shield className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase">
                      {team.team_name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        Rank #{index + 1}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="p-6 space-y-6">
                {/* Points */}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Points</span>
                  <span className="font-heading text-3xl font-black text-primary">
                    {team.points}
                  </span>
                </div>

                {/* Record */}
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-background rounded-lg p-3">
                    <div className="font-heading text-lg font-bold">
                      {team.played}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase">
                      Played
                    </div>
                  </div>
                  <div className="bg-background rounded-lg p-3">
                    <div className="font-heading text-lg font-bold text-green-500">
                      {team.won}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase">
                      Won
                    </div>
                  </div>
                  <div className="bg-background rounded-lg p-3">
                    <div className="font-heading text-lg font-bold text-muted-foreground">
                      {team.drawn}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase">
                      Draw
                    </div>
                  </div>
                  <div className="bg-background rounded-lg p-3">
                    <div className="font-heading text-lg font-bold text-red-500">
                      {team.lost}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase">
                      Lost
                    </div>
                  </div>
                </div>

                {/* Goals For */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">
                      Goals Scored
                    </span>
                    <span className="font-bold text-green-500">
                      {team.goals_for}
                    </span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${(team.goals_for / maxGoalsFor) * 100}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Goals Against */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">
                      Goals Conceded
                    </span>
                    <span className="font-bold text-red-500">
                      {team.goals_against}
                    </span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${(team.goals_against / maxGoalsAgainst) * 100}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Goal Difference */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <span className="text-muted-foreground">Goal Difference</span>
                  <div className="flex items-center gap-2">
                    {team.goal_difference > 0 ? (
                      <TrendingUp className="w-5 h-5 text-green-500" />
                    ) : team.goal_difference < 0 ? (
                      <TrendingDown className="w-5 h-5 text-red-500" />
                    ) : (
                      <Minus className="w-5 h-5 text-muted-foreground" />
                    )}
                    <span
                      className={`font-heading text-2xl font-black ${
                        team.goal_difference > 0
                          ? "text-green-500"
                          : team.goal_difference < 0
                          ? "text-red-500"
                          : "text-muted-foreground"
                      }`}
                    >
                      {team.goal_difference > 0 ? "+" : ""}
                      {team.goal_difference}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Target className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold uppercase mb-2">
            No Team Stats Yet
          </h3>
          <p className="text-muted-foreground">
            Add teams and complete matches to see statistics
          </p>
        </div>
      )}
    </div>
  );
}
